import { useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, Check, X } from 'lucide-react';
import {
  PASSWORD_REQUIREMENTS,
  passwordStrengthScore,
  passwordsMatch as computePasswordsMatch,
} from '@/lib/passwordRequirements';

// How long to wait after either field's input event before trusting the DOM
// as the source of truth (see the resync comment below). Long enough for
// Chrome to finish writing both fields, short enough that normal typing
// never feels laggy.
const AUTOFILL_SETTLE_MS = 60;

/**
 * Shared password + confirm-password fields with a strength meter and
 * requirements checklist -- used by VerifyEmailPage.js (standard flow) and
 * SignUpPage.js's trusted-link instant signup, so both stay visually and
 * behaviorally identical.
 */
export default function PasswordSetupFields({
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  showPassword,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
}) {
  const passwordStrength = passwordStrengthScore(password);
  const matches = computePasswordsMatch(password, confirmPassword);

  const passwordRef = useRef(null);
  const confirmRef = useRef(null);

  // Chrome's "Use Strong Password" flow fills both this field and Confirm
  // Password, but not always via two clean, back-to-back native input
  // events on both fields -- deferring each field's OWN onChange to a
  // microtask wasn't enough to stop one field's value getting wiped.
  // Instead of trying to guess Chrome's exact event sequence, treat the DOM
  // as the source of truth: on ANY input event on either field, wait for
  // things to settle, then read BOTH inputs' live .value directly off the
  // ref and commit both to state together. This is self-healing regardless
  // of which field(s) actually fired an event or in what order Chrome wrote
  // to them.
  const resyncFromDom = () => {
    if (passwordRef.current) setPassword(passwordRef.current.value);
    if (confirmRef.current) setConfirmPassword(confirmRef.current.value);
  };

  const handleInput = () => {
    setTimeout(resyncFromDom, AUTOFILL_SETTLE_MS);
  };

  return (
    <>
      {/* Password */}
      <div className="space-y-2">
        <Label htmlFor="password" className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-muted-foreground" />
          Password
        </Label>
        <div className="relative">
          <Input
            ref={passwordRef}
            id="password"
            name="new-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a strong password"
            defaultValue={password}
            onChange={handleInput}
            autoComplete="new-password"
            required
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        {/* Password Strength Indicator */}
        {password.length > 0 && (
          <div className="mt-2 p-3 bg-secondary rounded-lg border border-border">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    passwordStrength <= 2 ? 'bg-destructive/100' :
                    passwordStrength <= 3 ? 'bg-warning' :
                    passwordStrength <= 4 ? 'bg-info/100' : 'bg-success/100'
                  }`}
                  style={{ width: `${(passwordStrength / PASSWORD_REQUIREMENTS.length) * 100}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground w-16">
                {passwordStrength <= 2 ? 'Weak' :
                 passwordStrength <= 3 ? 'Fair' :
                 passwordStrength <= 4 ? 'Good' : 'Strong'}
              </span>
            </div>
            <ul className="space-y-1">
              {PASSWORD_REQUIREMENTS.map((req, idx) => (
                <li key={idx} className="flex items-center gap-2 text-xs">
                  {req.test(password) ? (
                    <Check className="w-3 h-3 text-success" />
                  ) : (
                    <X className="w-3 h-3 text-muted-foreground" />
                  )}
                  <span className={req.test(password) ? 'text-success' : 'text-muted-foreground'}>
                    {req.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Confirm Password */}
      <div className="space-y-2">
        <Label htmlFor="confirm_password" className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-muted-foreground" />
          Confirm Password
        </Label>
        <div className="relative">
          <Input
            ref={confirmRef}
            id="confirm_password"
            name="confirm-new-password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm your password"
            defaultValue={confirmPassword}
            onChange={handleInput}
            autoComplete="new-password"
            required
            className={`pr-10 ${
              confirmPassword.length > 0
                ? matches
                  ? 'border-success focus:border-success'
                  : 'border-destructive focus:border-destructive'
                : ''
            }`}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
            tabIndex={-1}
          >
            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {confirmPassword.length > 0 && !matches && (
          <p className="text-xs text-destructive">Passwords do not match</p>
        )}
      </div>
    </>
  );
}
